import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom, take } from 'rxjs';
import { IntelligenceModels } from '@models/intelligence.model';
import { PersonModel } from '@models/person.model';
import { PersonActions } from '@states/person/person.actions';
import { ConfirmDialogModel, ConfirmDialogService, UiButtonComponent, UiPersonTileComponent } from '@ui';
import { AuthSrcDirective } from '../../../../directives/auth-src.directive';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { PersonDialogService } from '../../../../../services/person-dialog.service';
import { api } from '@consts/url.const';

@Component({
  selector: 'app-persons-result',
  standalone: true,
  imports: [
    CommonModule,
    AuthSrcDirective,
    UiPersonTileComponent,
    UiButtonComponent,
    TranslatePipe,
    MatTooltipModule,
  ],
  templateUrl: './persons-result.component.html',
  styleUrls: ['./persons-result.component.scss'],
})
export class PersonsResultComponent {
  private readonly httpClient = inject(HttpClient);
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly store = inject(Store);
  private readonly personDialogService = inject(PersonDialogService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  /** Read directly from the service — self-updates without any parent binding. */
  readonly response = computed<IntelligenceModels.Persons.Response | null>(
    () => {
      const result = this.resultsService.current();
      if (result?.type !== 'persons') return null;
      return result.data as IntelligenceModels.Persons.Response;
    },
  );

  readonly persons = computed(() => this.response()?.persons ?? []);

  readonly selectedPerson = signal<IntelligenceModels.Persons.Person | null>(null);
  readonly searchingAppearances = signal(false);

  constructor() {
    // Close the detail view whenever new persons data arrives
    let prev: IntelligenceModels.Persons.Response | null = null;
    effect(
      () => {
        const current = this.response();
        if (current !== prev) {
          prev = current;
          this.selectedPerson.set(null);
          this.searchingAppearances.set(false);
        }
      },
      { allowSignalWrites: true },
    );
  }

  openDetail(person: IntelligenceModels.Persons.Person): void {
    this.selectedPerson.set(person);
  }

  closeDetail(): void {
    this.selectedPerson.set(null);
  }

  /**
   * Persist recognition through the production path (confirm dialog +
   * trainSavedPerson). The snapshot is patched centrally on success by
   * IntelligenceResultsService.
   */
  recognize(person: IntelligenceModels.Persons.Person): void {
    if (person.trainingStatus === 1 || !person.personId) return;
    const personId = person.personId;
    this.personDialogService
      .openConfirmRecognition({ personId })
      .afterClosed()
      .pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((recognitionResult) =>
        this.store.dispatch(
          PersonActions.trainSavedPerson({
            request: {
              personId,
              positiveTrackerIds: recognitionResult.positiveTrackerIds,
              negativeTrackerIds: recognitionResult.negativeTrackerIds,
            },
          }),
        ),
      );
  }

  /** Rename through the production edit dialog; snapshot patched on success. */
  edit(person: IntelligenceModels.Persons.Person): void {
    if (!person.personId) return;
    this.personDialogService.openEditPerson({
      personId: person.personId,
      name: person.name,
      bestImage: person.bestImage ?? '',
      trainingStatus: person.trainingStatus ?? PersonModel.TrainingStatus.Untrained,
    });
  }

  /** Delete through the production confirm + bulkDelete flow. */
  delete(person: IntelligenceModels.Persons.Person): void {
    if (!person.personId) return;
    const personIds = [person.personId];
    const msg = this.translate
      .instant('PERSONS.DELETE_PERSON_CONFIRM_MESSAGE', { peopleCount: 1, personName: person.name })
      .replace(/\n/g, '<br>');
    this.confirmDialog
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translate.instant('PERSONS.DELETE_PERSON_CONFIRM_TITLE'),
        msg,
        confirm: this.translate.instant('COMMON.DELETE'),
        cancel: this.translate.instant('COMMON.CANCEL'),
        disableClose: true,
      })
      .pipe(
        take(1),
        filter(
          (result) =>
            result?.selection === ConfirmDialogModel.ConfirmResult.OK &&
            result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.store.dispatch(PersonActions.bulkDeleteSavedPersons({ personIds })));
  }

  formatLastSeen(ts?: number): string {
    if (!ts) return '—';
    return new Date(ts).toLocaleString();
  }

  formatNumber(value: number | null | undefined): string {
    if (value == null) return '0';
    return value.toLocaleString();
  }

  formatTrainingStatus(status: number | null | undefined): string {
    switch (status) {
      case 1:
        return 'Trained';
      case 0:
        return 'Not Trained';
      default:
        return status != null ? 'Training…' : '—';
    }
  }

  initials(person: IntelligenceModels.Persons.Person): string {
    return (person.name ?? '?').charAt(0).toUpperCase();
  }

  async searchAppearances(person: IntelligenceModels.Persons.Person): Promise<void> {
    if (this.searchingAppearances() || !person.personId) return;
    this.searchingAppearances.set(true);
    try {
      const params = new HttpParams().set('personId', String(person.personId));
      const response = await firstValueFrom(
        this.httpClient.get<IntelligenceModels.Search.Response>(
          `${api.intelligence.search}/person`,
          { params },
        ),
      );
      const r = response as IntelligenceModels.Search.Response & {
        totalCount?: number;
      };
      const count = r.totalCount ?? r.results?.length ?? 0;
      const label = `${count} appearance${count !== 1 ? 's' : ''} — ${person.name ?? 'person'}`;
      // Dismiss the person detail panel before showing results
      this.selectedPerson.set(null);
      // Use overlay so this drill-down does not insert a new history entry
      // and break the sequential message↔history mapping.
      this.resultsService.setOverlay(response, 'search', label);
    } catch {
      this.searchingAppearances.set(false);
    }
  }
}
